/**
 * useMaterialRequirements Hook
 *
 * Fetches and calculates material requirements from shadow batches.
 * Uses the production_material_requirements database view.
 *
 * Part of US-P2-010: Process Recipe Management
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { supabase } from '@/supabase-client'
import { toLocalDateString } from '../utils/dateUtils'

/**
 * Hook to fetch material requirements for shadow batches
 * @param {Object} options - Filter options
 * @param {string} options.batchId - Filter by specific batch ID
 * @param {string} options.strainId - Filter by strain ID
 * @param {string} options.recipeType - Filter by recipe type ('block', 'spawn', 'liquid_culture', 'other')
 * @param {Date|string} options.startDate - Filter requirements starting from date
 * @param {Date|string} options.endDate - Filter requirements up to date
 * @param {boolean} options.shortfallsOnly - Only show items with shortfalls (default: false)
 * @returns {Object} { requirements, shortfalls, consolidated, loading, error, refetch, ... }
 */
export function useMaterialRequirements(options = {}) {
  const [requirements, setRequirements] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const {
    batchId = null,
    strainId = null,
    recipeType = null,
    startDate = null,
    endDate = null,
    shortfallsOnly = false
  } = options

  const fetchRequirements = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      let query = supabase
        .from('production_material_requirements')
        .select('*')

      if (batchId) {
        query = query.eq('batch_id', batchId)
      }

      if (strainId) {
        query = query.eq('strain_id', strainId)
      }

      if (recipeType) {
        query = query.eq('recipe_type', recipeType)
      }

      if (startDate) {
        const start = typeof startDate === 'string' ? startDate : toLocalDateString(startDate)
        query = query.gte('planned_start_date', start)
      }

      if (endDate) {
        const end = typeof endDate === 'string' ? endDate : toLocalDateString(endDate)
        query = query.lte('planned_start_date', end)
      }

      if (shortfallsOnly) {
        query = query.gt('quantity_shortfall', 0)
      }

      const { data, error: fetchError } = await query

      if (fetchError) {
        throw fetchError
      }

      setRequirements(data || [])
    } catch (err) {
      console.error('[useMaterialRequirements] Error:', err)
      setError(err.message)
      setRequirements([])
    } finally {
      setLoading(false)
    }
  }, [batchId, strainId, recipeType, startDate, endDate, shortfallsOnly])

  useEffect(() => {
    fetchRequirements()
  }, [fetchRequirements])

  /**
   * Get items that have shortfalls (quantity_shortfall > 0)
   */
  const shortfalls = useMemo(() => {
    return requirements.filter(r => Number(r.quantity_shortfall) > 0)
  }, [requirements])

  /**
   * Get consolidated requirements by inventory item (sum across batches)
   * Useful for procurement planning
   */
  const consolidated = useMemo(() => {
    const itemMap = new Map()

    requirements.forEach(req => {
      const key = req.inventory_item_id
      const existing = itemMap.get(key)

      if (existing) {
        existing.total_quantity_required += Number(req.quantity_required)
        existing.total_shortfall += Number(req.quantity_shortfall)
        existing.batch_count += 1
        existing.batches.push({
          batch_id: req.batch_id,
          batch_code: req.batch_code,
          phase_name: req.phase_name,
          planned_start_date: req.planned_start_date,
          quantity_required: Number(req.quantity_required)
        })
      } else {
        itemMap.set(key, {
          inventory_item_id: req.inventory_item_id,
          item_name: req.item_name,
          item_category: req.item_category,
          unit: req.unit,
          total_quantity_required: Number(req.quantity_required),
          current_stock: Number(req.current_stock),
          total_shortfall: Number(req.quantity_shortfall),
          batch_count: 1,
          batches: [{
            batch_id: req.batch_id,
            batch_code: req.batch_code,
            phase_name: req.phase_name,
            planned_start_date: req.planned_start_date,
            quantity_required: Number(req.quantity_required)
          }]
        })
      }
    })

    return Array.from(itemMap.values()).sort((a, b) => {
      // Sort by shortfall descending (most critical first)
      if (b.total_shortfall !== a.total_shortfall) {
        return b.total_shortfall - a.total_shortfall
      }
      // Then by item name
      return a.item_name.localeCompare(b.item_name)
    })
  }, [requirements])

  /**
   * Get consolidated items with shortfalls only
   */
  const consolidatedShortfalls = useMemo(() => {
    return consolidated.filter(item => item.total_shortfall > 0)
  }, [consolidated])

  /**
   * Check if all materials are available for a specific batch
   * @param {string} checkBatchId - Batch ID to check
   * @returns {Object} { isAvailable, missingItems, requiredItems }
   */
  const checkBatchMaterials = useCallback((checkBatchId) => {
    const batchRequirements = requirements.filter(r => r.batch_id === checkBatchId)

    if (batchRequirements.length === 0) {
      return {
        isAvailable: true,
        hasRequirements: false,
        missingItems: [],
        requiredItems: []
      }
    }

    const missingItems = batchRequirements.filter(r =>
      Number(r.quantity_shortfall) > 0 && !r.is_optional
    )

    return {
      isAvailable: missingItems.length === 0,
      hasRequirements: true,
      missingItems: missingItems.map(r => ({
        inventory_item_id: r.inventory_item_id,
        item_name: r.item_name,
        quantity_required: Number(r.quantity_required),
        current_stock: Number(r.current_stock),
        shortfall: Number(r.quantity_shortfall),
        unit: r.unit
      })),
      requiredItems: batchRequirements.map(r => ({
        inventory_item_id: r.inventory_item_id,
        item_name: r.item_name,
        quantity_required: Number(r.quantity_required),
        current_stock: Number(r.current_stock),
        shortfall: Number(r.quantity_shortfall),
        unit: r.unit,
        is_optional: r.is_optional,
        is_available: Number(r.quantity_shortfall) === 0
      }))
    }
  }, [requirements])

  /**
   * Get requirements grouped by batch
   */
  const byBatch = useMemo(() => {
    const batchMap = new Map()

    requirements.forEach(req => {
      const key = req.batch_id
      const existing = batchMap.get(key)

      if (existing) {
        existing.items.push(req)
        existing.total_shortfall += Number(req.quantity_shortfall)
        if (Number(req.quantity_shortfall) > 0 && !req.is_optional) {
          existing.has_critical_shortfall = true
        }
      } else {
        batchMap.set(key, {
          batch_id: req.batch_id,
          batch_code: req.batch_code,
          strain_id: req.strain_id,
          strain_name: req.strain_name,
          phase_name: req.phase_name,
          planned_start_date: req.planned_start_date,
          planned_end_date: req.planned_end_date,
          total_blocks: req.total_blocks,
          recipe_name: req.recipe_name,
          recipe_type: req.recipe_type,
          items: [req],
          total_shortfall: Number(req.quantity_shortfall),
          has_critical_shortfall: Number(req.quantity_shortfall) > 0 && !req.is_optional
        })
      }
    })

    return Array.from(batchMap.values()).sort((a, b) => {
      // Sort by planned start date ascending
      return new Date(a.planned_start_date) - new Date(b.planned_start_date)
    })
  }, [requirements])

  /**
   * Get summary statistics
   */
  const summary = useMemo(() => {
    const uniqueBatches = new Set(requirements.map(r => r.batch_id))
    const uniqueItems = new Set(requirements.map(r => r.inventory_item_id))
    const itemsWithShortfall = new Set(shortfalls.map(r => r.inventory_item_id))
    const batchesWithShortfall = new Set(shortfalls.map(r => r.batch_id))

    return {
      total_batches: uniqueBatches.size,
      total_items: uniqueItems.size,
      items_with_shortfall: itemsWithShortfall.size,
      batches_with_shortfall: batchesWithShortfall.size,
      total_requirements: requirements.length,
      total_shortfall_count: shortfalls.length
    }
  }, [requirements, shortfalls])

  return {
    // Raw data
    requirements,
    // Filtered/derived data
    shortfalls,
    consolidated,
    consolidatedShortfalls,
    byBatch,
    summary,
    // State
    loading,
    error,
    // Methods
    fetchRequirements,
    checkBatchMaterials,
    refetch: fetchRequirements
  }
}

/**
 * Hook to check material availability for a single batch
 * Lighter-weight hook when you only need to check one batch
 * @param {string} batchId - Batch ID to check
 * @returns {Object} { isAvailable, missingItems, requiredItems, loading, error, refetch }
 */
export function useBatchMaterialCheck(batchId) {
  const [result, setResult] = useState({
    isAvailable: true,
    hasRequirements: false,
    missingItems: [],
    requiredItems: []
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const checkMaterials = useCallback(async () => {
    if (!batchId) {
      setResult({
        isAvailable: true,
        hasRequirements: false,
        missingItems: [],
        requiredItems: []
      })
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const { data, error: fetchError } = await supabase
        .from('production_material_requirements')
        .select('*')
        .eq('batch_id', batchId)

      if (fetchError) {
        throw fetchError
      }

      if (!data || data.length === 0) {
        setResult({
          isAvailable: true,
          hasRequirements: false,
          missingItems: [],
          requiredItems: []
        })
        return
      }

      const missingItems = data.filter(r =>
        Number(r.quantity_shortfall) > 0 && !r.is_optional
      )

      setResult({
        isAvailable: missingItems.length === 0,
        hasRequirements: true,
        missingItems: missingItems.map(r => ({
          inventory_item_id: r.inventory_item_id,
          item_name: r.item_name,
          quantity_required: Number(r.quantity_required),
          current_stock: Number(r.current_stock),
          shortfall: Number(r.quantity_shortfall),
          unit: r.unit
        })),
        requiredItems: data.map(r => ({
          inventory_item_id: r.inventory_item_id,
          item_name: r.item_name,
          quantity_required: Number(r.quantity_required),
          current_stock: Number(r.current_stock),
          shortfall: Number(r.quantity_shortfall),
          unit: r.unit,
          is_optional: r.is_optional,
          is_available: Number(r.quantity_shortfall) === 0
        }))
      })
    } catch (err) {
      console.error('[useBatchMaterialCheck] Error:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [batchId])

  useEffect(() => {
    checkMaterials()
  }, [checkMaterials])

  return {
    ...result,
    loading,
    error,
    refetch: checkMaterials
  }
}

export default useMaterialRequirements
